// formatUtils.js - Shared formatting utilities for the application

/**
 * Utility class for formatting various data types consistently across the application
 */
export class FormatUtils {
    /**
     * Format currency with appropriate symbol and separators
     * @param {number|string} amount - The amount to format
     * @param {string} currency - Currency code (default: 'KES')
     * @param {boolean} showSymbol - Whether to show currency symbol
     * @returns {string} Formatted currency string
     */
    static formatCurrency(amount, currency = 'KES', showSymbol = true) {
        if (amount === null || amount === undefined || amount === '') {
            return '-';
        }

        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

        if (isNaN(numAmount)) {
            return '-';
        }

        // Format with thousand separators
        const formatted = numAmount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        return showSymbol ? `${currency} ${formatted}` : formatted;
    }

    /**
     * Format large numbers with K, M, B suffixes
     * @param {number|string} num - Number to format
     * @param {number} decimals - Number of decimal places
     * @returns {string} Formatted number with suffix
     */
    static formatCompactNumber(num, decimals = 1) {
        if (num === null || num === undefined || num === '') {
            return '0';
        }

        const number = typeof num === 'string' ? parseFloat(num) : num;

        if (isNaN(number)) {
            return '0';
        }

        if (number < 1000) {
            return number.toString();
        }

        const units = ['K', 'M', 'B', 'T'];
        let unitIndex = -1;
        let scaledNumber = number;

        while (scaledNumber >= 1000 && unitIndex < units.length - 1) {
            scaledNumber /= 1000;
            unitIndex++;
        }

        return scaledNumber.toFixed(decimals) + units[unitIndex];
    }

    /**
     * Format phone numbers consistently
     * @param {string} phone - Phone number to format
     * @param {string} defaultCountryCode - Default country code if not present
     * @returns {string} Formatted phone number
     */
    static formatPhoneNumber(phone, defaultCountryCode = '+254') {
        if (!phone) return '-';

        // Remove all non-digit characters
        let cleaned = phone.replace(/\D/g, '');

        // Handle different formats
        if (cleaned.startsWith('254')) {
            // Already has country code
            return '+' + cleaned.slice(0, 3) + ' ' + cleaned.slice(3, 6) + ' ' + cleaned.slice(6);
        } else if (cleaned.startsWith('0')) {
            // Local format starting with 0
            cleaned = cleaned.slice(1);
            return defaultCountryCode + ' ' + cleaned.slice(0, 3) + ' ' + cleaned.slice(3);
        } else if (cleaned.length === 9) {
            // Just the number without country code or leading 0
            return defaultCountryCode + ' ' + cleaned.slice(0, 3) + ' ' + cleaned.slice(3);
        }

        // Return as-is if format is not recognized
        return phone;
    }

    /**
     * Format date consistently across the application
     * @param {string|Date} date - Date to format
     * @param {string} format - Format type: 'short', 'long', 'full', 'relative'
     * @returns {string} Formatted date string
     */
    static formatDate(date, format = 'short') {
        if (!date) return '-';

        const dateObj = typeof date === 'string' ? new Date(date) : date;

        if (isNaN(dateObj.getTime())) {
            return '-';
        }

        switch (format) {
            case 'short':
                // DD/MM/YYYY
                return dateObj.toLocaleDateString('en-GB');

            case 'long':
                // 15 January 2024
                return dateObj.toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });

            case 'full':
                // Monday, 15 January 2024
                return dateObj.toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });

            case 'relative':
                return this.getRelativeTime(dateObj);

            default:
                return dateObj.toLocaleDateString();
        }
    }

    /**
     * Get relative time string (e.g., '2 hours ago', 'yesterday')
     * @param {Date} date - Date to compare
     * @returns {string} Relative time string
     */
    static getRelativeTime(date) {
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) {
            return 'just now';
        }

        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) {
            return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
        }

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) {
            return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
        }

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays === 1) {
            return 'yesterday';
        }
        if (diffInDays < 7) {
            return `${diffInDays} days ago`;
        }
        if (diffInDays < 30) {
            const weeks = Math.floor(diffInDays / 7);
            return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
        }
        if (diffInDays < 365) {
            const months = Math.floor(diffInDays / 30);
            return `${months} month${months !== 1 ? 's' : ''} ago`;
        }

        const years = Math.floor(diffInDays / 365);
        return `${years} year${years !== 1 ? 's' : ''} ago`;
    }

    /**
     * Format percentage with proper decimals and symbol
     * @param {number} value - Value to format as percentage
     * @param {number} decimals - Number of decimal places
     * @param {boolean} includeSign - Whether to include + or - sign
     * @returns {string} Formatted percentage
     */
    static formatPercentage(value, decimals = 1, includeSign = false) {
        if (value === null || value === undefined || isNaN(value)) {
            return '0%';
        }

        const formatted = value.toFixed(decimals);
        const sign = includeSign && value > 0 ? '+' : '';

        return `${sign}${formatted}%`;
    }

    /**
     * Format file size in human-readable format
     * @param {number} bytes - File size in bytes
     * @param {number} decimals - Number of decimal places
     * @returns {string} Formatted file size
     */
    static formatFileSize(bytes, decimals = 2) {
        if (bytes === 0) return '0 Bytes';
        if (!bytes) return '-';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
    }

    /**
     * Format name consistently (handle first, middle, last)
     * @param {object} nameObj - Object with firstName, middleName, lastName
     * @param {string} format - Format type: 'full', 'short', 'initials'
     * @returns {string} Formatted name
     */
    static formatName(nameObj, format = 'full') {
        const { firstName = '', middleName = '', lastName = '' } = nameObj;

        switch (format) {
            case 'full':
                return [firstName, middleName, lastName]
                    .filter(Boolean)
                    .join(' ')
                    .trim() || '-';

            case 'short':
                // First name and last initial
                const lastInitial = lastName ? `${lastName[0]}.` : '';
                return [firstName, lastInitial]
                    .filter(Boolean)
                    .join(' ')
                    .trim() || '-';

            case 'initials':
                const initials = [
                    firstName ? firstName[0] : '',
                    middleName ? middleName[0] : '',
                    lastName ? lastName[0] : ''
                ].filter(Boolean).join('');
                return initials.toUpperCase() || '-';

            default:
                return [firstName, middleName, lastName]
                    .filter(Boolean)
                    .join(' ')
                    .trim() || '-';
        }
    }

    /**
     * Format status with consistent styling
     * @param {string} status - Status value
     * @param {object} statusConfig - Configuration for status colors
     * @returns {object} Object with formatted text and style classes
     */
    static formatStatus(status, statusConfig = {}) {
        const defaultConfig = {
            ACTIVE: { text: 'Active', class: 'bg-green-100 text-green-800' },
            INACTIVE: { text: 'Inactive', class: 'bg-gray-100 text-gray-800' },
            PENDING: { text: 'Pending', class: 'bg-yellow-100 text-yellow-800' },
            SUSPENDED: { text: 'Suspended', class: 'bg-orange-100 text-orange-800' },
            CANCELLED: { text: 'Cancelled', class: 'bg-red-100 text-red-800' },
            COMPLETED: { text: 'Completed', class: 'bg-blue-100 text-blue-800' }
        };

        const config = { ...defaultConfig, ...statusConfig };
        const statusUpper = status?.toUpperCase();

        return config[statusUpper] || {
            text: status || 'Unknown',
            class: 'bg-gray-100 text-gray-800'
        };
    }

    /**
     * Format ID numbers (mask sensitive parts)
     * @param {string} id - ID number
     * @param {boolean} mask - Whether to mask part of the ID
     * @returns {string} Formatted ID
     */
    static formatIdNumber(id, mask = false) {
        if (!id) return '-';

        const idStr = id.toString();

        if (mask && idStr.length > 4) {
            const visibleStart = 2;
            const visibleEnd = 2;
            const maskedSection = '*'.repeat(idStr.length - visibleStart - visibleEnd);
            return idStr.slice(0, visibleStart) + maskedSection + idStr.slice(-visibleEnd);
        }

        return idStr;
    }

    /**
     * Format email (optionally mask for privacy)
     * @param {string} email - Email address
     * @param {boolean} mask - Whether to mask part of the email
     * @returns {string} Formatted email
     */
    static formatEmail(email, mask = false) {
        if (!email) return '-';

        if (mask) {
            const [localPart, domain] = email.split('@');
            if (localPart && domain) {
                const maskedLocal = localPart.length > 2
                    ? localPart[0] + '*'.repeat(Math.max(1, localPart.length - 2)) + localPart[localPart.length - 1]
                    : localPart;
                return `${maskedLocal}@${domain}`;
            }
        }

        return email.toLowerCase();
    }

    /**
     * Truncate text with ellipsis
     * @param {string} text - Text to truncate
     * @param {number} maxLength - Maximum length
     * @param {string} suffix - Suffix to add (default: '...')
     * @returns {string} Truncated text
     */
    static truncateText(text, maxLength = 50, suffix = '...') {
        if (!text) return '';
        if (text.length <= maxLength) return text;

        return text.slice(0, maxLength - suffix.length).trim() + suffix;
    }
}

// Export as default as well for convenience
export default FormatUtils;
